import { useState, useEffect } from 'react';
import { db } from '../utils/firebase/client';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { GradientCard } from './ui/GradientCard';
import { Button } from './ui/button';
import {
  User,
  Bell,
  Shield,
  HelpCircle,
  Palette,
  Database,
  Globe,
  ListTodo,
  LogOut,
  Users,
  Wrench,
  Map as MapIcon,
  Zap,
  CheckSquare,
  Navigation,
  AlertTriangle,
  Copy,
  DollarSign,
  Building2,
  Image,
  Sparkles,
  CheckCircle,
  Smartphone,
  Moon,
  Activity,
  Settings as SettingsIcon,
  Package,
  ArrowRightLeft,
  MessagesSquare
} from 'lucide-react';
import AdminWellsManager from './admin/AdminWellsManager';
import ComprehensiveWellForm from './ComprehensiveWellForm';
import UserManagement from './UserManagement';
import SecuritySetup from './SecuritySetup';
import AboutModal from './AboutModal';
import VendorManagement from './VendorManagement';
import DataStorageBackup from './DataStorageBackup';
import NotificationPolicyEditor from './settings/NotificationPolicyEditor';
import NotificationTemplateEditor from './settings/NotificationTemplateEditor';
import DeliveryTrackingDashboard from './settings/DeliveryTrackingDashboard';
import InventoryManagement from './inventory/InventoryManagement';
import EscalationMatrix from './EscalationMatrix';
import NotificationSettings from './NotificationSettings';
import VoiceAISettings from './settings/VoiceAISettings';
import { toast } from 'sonner';
import MapsAppSelectionDialog, { MapsApp } from './MapsAppSelectionDialog';
import WellCheckDashboard from './wells/WellCheckDashboard';
import { updateNavigationMode, updateDefaultMapsApp } from '../utils/userPreferences';


interface SettingsTabProps {
  user: any;
  wells: any[];
  companyName?: string;
  pumpers?: any[];
  onWellsUpdate?: (wells: any[]) => void;
  onShowMessaging?: () => void;
  onShowShiftHandoff?: () => void;
  onShowTaskManager?: () => void;
  onShowConflictResolver?: () => void;
  onShowRouteOptimizer?: () => void;
  onShowVoiceAssistant?: () => void;
  onShowTimeClock?: () => void;
  onShowServiceTickets?: () => void;
  onShowNotificationSettings?: () => void;
  onNavigate?: (screen: string) => void;
  onUserUpdate?: (updatedUser: any) => void;
  defaultSection?: 'main' | 'wells' | 'users' | 'security' | 'appearance' | 'datastorage' | 'help' | 'wellchecks' | 'vendors' | 'notificationPolicies' | 'deliveryTracking';
  isDarkMode?: boolean;
  onToggleTheme?: () => void;
}
export default function SettingsTab({
  user,
  wells,
  companyName,
  pumpers = [],
  onWellsUpdate,
  onShowVoiceAssistant: _onShowVoiceAssistant, // Reserved for future use
  onShowTaskManager,
  onShowShiftHandoff,
  onShowMessaging,
  onNavigate,
  onUserUpdate,
  defaultSection = 'main',
  isDarkMode = false,
  onToggleTheme = () => { },
}: SettingsTabProps) {
  const [activeSection, setActiveSection] = useState<
    'main' | 'wells' | 'users' | 'security' | 'appearance' | 'navigation' | 'help' | 'wellchecks' | 'vendors' | 'datastorage' | 'notificationPolicies' | 'notificationTemplates' | 'deliveryTracking' | 'inventory' | 'escalationMatrix' | 'notifications' | 'voice'
  >(defaultSection);
  const [showWellForm, setShowWellForm] = useState(false);
  const [selectedWell, setSelectedWell] = useState<any>(null);
  const [showAboutModal, setShowAboutModal] = useState(false);
  const [showMapsAppDialog, setShowMapsAppDialog] = useState(false);
  const [resolvedVendorCompanyId, setResolvedVendorCompanyId] = useState<string>('');
  const [resolvedVendorCompanyName, setResolvedVendorCompanyName] = useState<string>('');

  useEffect(() => {
    const resolveCompany = async () => {
      const currentId = user.companyId || user.companyId || user.companyId || 'default';

      try {
        const { collection, getDocs, query, where, doc, getDoc } = await import('firebase/firestore');

        // First, try to fetch company doc directly by ID (if currentId is the slug)
        const directDocRef = doc(db, 'companies', currentId);
        const directDoc = await getDoc(directDocRef);

        if (directDoc.exists()) {
          const companyData = directDoc.data();
          setResolvedVendorCompanyId(companyData?.companyId || currentId);
          if (companyData?.companyName) {
            setResolvedVendorCompanyName(companyData.companyName);
          }
          return;
        }

        // If not found directly, currentId might be a companyId - search by 'companyId' field
        const companiesQuery = query(
          collection(db, 'companies'),
          where('companyId', '==', currentId)
        );
        const snapshot = await getDocs(companiesQuery);

        if (!snapshot.empty) {
          const companyDoc = snapshot.docs[0];
          const companyData = companyDoc.data();
          setResolvedVendorCompanyId(companyData?.companyId || companyDoc.id);
          if (companyData?.companyName) {
            setResolvedVendorCompanyName(companyData.companyName);
          }
          return;
        }

        // Fallback: try 'id' field (some legacy documents)
        const companiesQuery2 = query(
          collection(db, 'companies'),
          where('id', '==', currentId)
        );
        const snapshot2 = await getDocs(companiesQuery2);

        if (!snapshot2.empty) {
          const companyDoc = snapshot2.docs[0];
          const companyData = companyDoc.data();
          setResolvedVendorCompanyId(companyData?.companyId || companyDoc.id);
          if (companyData?.companyName) {
            setResolvedVendorCompanyName(companyData.companyName);
          }
          return;
        }

        setResolvedVendorCompanyId(currentId);
      } catch (e) {
        setResolvedVendorCompanyId(currentId);
      }
    };

    if (user?.companyId || user?.companyId || user?.companyId) {
      resolveCompany();
    }
  }, [user]);

  // Simple theme state
  const [fontScale, setFontScale] = useState(1.0);
  const [highContrast, setHighContrast] = useState(false);

  const isAdmin = user?.role === 'admin' || user?.role === 'manager' || user?.email?.includes('admin');
  const userRole = user?.role || 'viewer';

  // Helper function to detect platform
  const getPlatform = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.includes('iphone') || userAgent.includes('ipad') || userAgent.includes('mac')) {
      return 'ios';
    }
    if (userAgent.includes('android')) {
      return 'android';
    }
    return 'other';
  };

  // Apply font scale to document
  useEffect(() => {
    document.documentElement.style.fontSize = `${fontScale * 100}%`;
    localStorage.setItem('oilwellx-font-scale', String(fontScale));
  }, [fontScale]);

  // Save high contrast preference
  useEffect(() => {
    localStorage.setItem('oilwellx-high-contrast', String(highContrast));
  }, [highContrast]);

  const handleSaveWell = () => {
    // ComprehensiveWellForm handles its own Firebase save
    // This callback just refreshes the wells list and closes the form
    setShowWellForm(false);
    setSelectedWell(null);
    // Trigger a refresh of wells data
    onWellsUpdate?.(wells); // Signal parent to refresh
  };

  const handleDeleteWell = (wellId: string) => {
    const updatedWells = wells.filter((w) => w.id !== wellId);
    onWellsUpdate?.(updatedWells);
    toast.success('Well deleted successfully');
  };

  const handleViewWell = (well: any) => {
    toast.info(`Viewing ${well.name}`);
    // In production, navigate to well details
  };

  // Get company ID safely
  const getCompanyIdLocal = () => {
    return user?.companyId || 'default';
  };

  // Get current navigation mode with default
  const getCurrentNavigationMode = () => {
    return user?.preferences?.navigationMode || 'internal';
  };

  // Navigation preference handlers
  const handleNavigationModeChange = async (mode: 'internal' | 'external') => {

    try {
      const companyId = getCompanyIdLocal();

      // Optimistically update UI first for immediate feedback
      const updatedUser = {
        ...user,
        preferences: {
          ...user.preferences,
          navigationMode: mode
        }
      };
      onUserUpdate?.(updatedUser);

      // Then save to database
      const success = await updateNavigationMode(user.id, mode, companyId);

      if (success) {
        toast.success(`Navigation mode set to ${mode === 'internal' ? 'In-App' : 'External'}`);

        // If switching to external and no default app is set, show the selection dialog
        if (mode === 'external' && !user?.preferences?.defaultMapsApp) {
          setTimeout(() => {
            setShowMapsAppDialog(true);
          }, 500); // Small delay for better UX
        }
      } else {
        console.error('❌ Failed to save navigation mode to database');
        toast.error('Failed to save navigation preference');
        // Revert optimistic update
        onUserUpdate?.(user);
      }
    } catch (error) {
      console.error('❌ Error updating navigation mode:', error);
      toast.error('Failed to update navigation preference');
      // Revert optimistic update
      onUserUpdate?.(user);
    }
  };

  const handleMapsAppChange = async (app: MapsApp) => {

    try {
      const companyId = getCompanyIdLocal();

      // Optimistically update UI first
      const updatedUser = {
        ...user,
        preferences: {
          ...user.preferences,
          defaultMapsApp: app
        }
      };
      onUserUpdate?.(updatedUser);

      // Then save to database
      const success = await updateDefaultMapsApp(user.id, app, companyId);

      if (success) {
        toast.success(`Default maps app set to ${app === 'system' ? 'System Default' : app.charAt(0).toUpperCase() + app.slice(1)}`);
      } else {
        console.error('❌ Failed to save maps app preference');
        toast.error('Failed to save maps app preference');
        // Revert optimistic update
        onUserUpdate?.(user);
      }
    } catch (error) {
      console.error('❌ Error updating default maps app:', error);
      toast.error('Failed to update default maps app');
      // Revert optimistic update
      onUserUpdate?.(user);
    }
  };

  // Render main settings menu
  if (activeSection === 'main') {
    interface SectionItem {
      icon: any;
      label: string;
      sublabel: string;
      color: string;
      action: (() => void) | undefined;
      restricted?: boolean;
    }

    // Base sections available to EVERYONE (Pumpers & Users)
    const settingsSections: SectionItem[] = [
      { icon: Navigation, label: 'Navigation', sublabel: 'Maps & routing preferences', color: 'from-green-500 to-emerald-500', action: () => setActiveSection('navigation') },
      { icon: Bell, label: 'Notifications', sublabel: 'Alerts & reminders', color: 'from-orange-500 to-red-500', action: () => setActiveSection('notifications') },
      // Privacy/Security for users is usually just password change, but full SecuritySetup is Admin.
      // We'll keep a basic 'Security' tab if needed, but 'SecuritySetup' component is advanced.
      // For now, allow basic Appearance/Help.
      { icon: Palette, label: 'Appearance', sublabel: 'Theme & display', color: 'from-indigo-500 to-violet-500', action: () => setActiveSection('appearance') },
      { icon: HelpCircle, label: 'Help & Support', sublabel: 'Get assistance', color: 'from-amber-500 to-yellow-500', action: () => setActiveSection('help') },
      { icon: LogOut, label: 'Sign Out', sublabel: 'End your session', color: 'from-red-500 to-rose-500', action: () => { } },
    ];

    // 🔒 MANAGER & ADMIN ONLY SECTIONS
    // Only show these if user is explicitly a manager or admin
    if (userRole === 'manager' || userRole === 'admin' || userRole === 'superadmin') {
      // Insert after Notifications (index 2, since we removed User Management from base)
      settingsSections.splice(2, 0,
        { icon: User, label: 'User Management', sublabel: 'Profile, Users & Preferences', color: 'from-blue-500 to-cyan-500', action: () => setActiveSection('users'), restricted: true },
        { icon: Shield, label: 'Security & Privacy', sublabel: 'Data protection', color: 'from-purple-500 to-pink-500', action: () => setActiveSection('security') },
        { icon: Database, label: 'Data & Backup', sublabel: 'Sync & backup', color: 'from-emerald-500 to-green-500', action: () => setActiveSection('datastorage') },
        { icon: SettingsIcon, label: 'Notification Policy', sublabel: 'Configure channels & rules', color: 'from-rose-500 to-red-600', action: () => setActiveSection('notificationPolicies'), restricted: true },
        { icon: Shield, label: 'Escalation Matrix', sublabel: 'Alert escalation chains', color: 'from-amber-500 to-orange-600', action: () => setActiveSection('escalationMatrix'), restricted: true },
        { icon: Activity, label: 'Delivery Tracking', sublabel: 'Audit logs', color: 'from-blue-600 to-indigo-600', action: () => setActiveSection('deliveryTracking'), restricted: true }
      );

      // Admin Only
      if (userRole === 'admin' || userRole === 'superadmin') {
        settingsSections.splice(3, 0, { icon: Users, label: 'Vendor Management', sublabel: 'Suppliers & Contractors', color: 'from-cyan-600 to-blue-700', action: () => setActiveSection('vendors') });
      }
    }

    const quickActions: SectionItem[] = [
      { icon: AlertTriangle, label: 'Alerts', sublabel: 'View active alerts', color: 'from-orange-500 to-red-600', action: () => onNavigate?.('alerts') },
      { icon: MapIcon, label: 'Map', sublabel: 'View wells on map', color: 'from-emerald-500 to-green-600', action: () => onNavigate?.('map') },
      { icon: Globe, label: 'Satellite Command', sublabel: '3D Field Visualization', color: 'from-blue-600 to-indigo-600', action: () => onNavigate?.('satellite') },
      { icon: Sparkles, label: 'Voice & AI', sublabel: 'X-Assist & Persona', color: 'from-pink-500 to-rose-500', action: () => setActiveSection('voice') },
      { icon: ListTodo, label: 'Tasks', sublabel: 'Task Manager', color: 'from-violet-500 to-purple-600', action: onShowTaskManager },
      { icon: ArrowRightLeft, label: 'Shift Handoff', sublabel: 'Transfer shift info', color: 'from-amber-500 to-yellow-600', action: onShowShiftHandoff },
      { icon: MessagesSquare, label: 'Messaging', sublabel: 'Team messages', color: 'from-sky-500 to-blue-600', action: onShowMessaging },
      { icon: Zap, label: 'Quick Entry', sublabel: 'Fast data input', color: 'from-blue-500 to-cyan-600', action: () => onNavigate?.('quickEntry') },
      { icon: Copy, label: 'Compare', sublabel: 'Compare wells', color: 'from-purple-500 to-violet-600', action: () => onNavigate?.('compare') },
      // Only show Billing, Company, Checks, Industry Map to admins/managers
      ...(isAdmin ? [
        { icon: DollarSign, label: 'Billing', sublabel: 'Invoices & payments', color: 'from-green-500 to-emerald-600', action: () => onNavigate?.('billing'), restricted: true },
        { icon: Building2, label: 'Company', sublabel: 'Company settings', color: 'from-indigo-500 to-blue-600', action: () => onNavigate?.('company'), restricted: true },
        { icon: CheckSquare, label: 'Checks', sublabel: 'Well checks', color: 'from-cyan-500 to-teal-600', action: () => setActiveSection('wellchecks'), restricted: true },
        { icon: Package, label: 'Inventory', sublabel: 'Parts & stock', color: 'from-amber-500 to-orange-600', action: () => setActiveSection('inventory'), restricted: true },
        { icon: Users, label: 'Vendors', sublabel: 'Manage vendors', color: 'from-pink-500 to-rose-500', action: () => setActiveSection('vendors'), restricted: true },
        { icon: Globe, label: 'Industry Map', sublabel: 'OCC data & all wells', color: 'from-teal-500 to-cyan-600', action: () => onNavigate?.('industryMap'), restricted: true },
      ] : []),
      { icon: Wrench, label: 'Equipment', sublabel: 'Manage equipment', color: 'from-gray-500 to-slate-600', action: () => onNavigate?.('equipment') },
      // Gallery accessible to everyone
      { icon: Image, label: 'Gallery', sublabel: 'Photo gallery', color: 'from-rose-500 to-pink-600', action: () => onNavigate?.('gallery') },
    ];

    return (
      <div className="px-6 py-8">
        {/* User Info Card with Gradient */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`
            relative overflow-hidden rounded-3xl mb-6 p-1 bg-gradient-to-br from-blue-500 to-purple-600
          `}
        >
          <div className={`
            ${isDarkMode ? 'bg-slate-900/95' : 'bg-white/95'}
            backdrop-blur-xl rounded-[1.3rem] p-6
          `}>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
                <User className="w-8 h-8 text-white" />
              </div>
              <div className="flex-1">
                <div className={`text-lg mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{user?.email || 'ryan@ryco.com'}</div>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-xs rounded-full border border-emerald-500/30">
                    {user?.role || 'Manager'}
                  </span>
                  <span className="px-2 py-0.5 bg-blue-500/20 text-blue-600 dark:text-blue-400 text-xs rounded-full border border-blue-500/30">
                    Premium
                  </span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Quick Actions Section */}
        <div className="mb-6">
          <h3 className={`text-sm font-semibold mb-3 px-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>TOOLS & FEATURES</h3>
          <div className="grid grid-cols-2 gap-3">
            {quickActions.map((action, index) => (
              <motion.button
                key={action.label}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.05, type: "spring", stiffness: 400, damping: 25 }}
                whileHover={{ scale: 1.05, y: -4 }}
                whileTap={{ scale: 0.98 }}
                onClick={action.action}
                className="relative overflow-hidden rounded-2xl p-1 bg-gradient-to-br"
                style={{
                  backgroundImage: `linear-gradient(135deg, ${action.color === 'from-orange-500 to-red-600' ? '#f97316, #dc2626' :
                    action.color === 'from-emerald-500 to-green-600' ? '#10b981, #16a34a' :
                      action.color === 'from-pink-500 to-rose-500' ? '#ec4899, #f43f5e' :
                        action.color === 'from-blue-500 to-cyan-600' ? '#3b82f6, #0891b2' :
                          action.color === 'from-purple-500 to-violet-600' ? '#a855f7, #7c3aed' :
                            action.color === 'from-green-500 to-emerald-600' ? '#22c55e, #059669' :
                              action.color === 'from-indigo-500 to-blue-600' ? '#6366f1, #2563eb' :
                                action.color === 'from-cyan-500 to-teal-600' ? '#06b6d4, #0d9488' :
                                  action.color === 'from-teal-500 to-cyan-600' ? '#14b8a6, #0891b2' :
                                    action.color === 'from-violet-500 to-purple-600' ? '#8b5cf6, #9333ea' :
                                      action.color === 'from-blue-600 to-indigo-600' ? '#2563eb, #4f46e5' :
                                        action.color === 'from-amber-500 to-orange-600' ? '#f59e0b, #ea580c' :
                                          action.color === 'from-rose-500 to-pink-600' ? '#f43f5e, #db2777' :
                                            action.color === 'from-gray-500 to-slate-600' ? '#6b7280, #475569' :
                                              '#f43f5e, #db2777'
                    })`
                }}
              >
                <div className={`
                  ${isDarkMode ? 'bg-slate-900/90' : 'bg-white/90'}
                  backdrop-blur rounded-[0.9rem] p-4 text-left h-full flex items-center gap-3 relative
                `}>
                  {/* Restricted Icon */}
                  {action.restricted && (
                    <div className="absolute top-2 right-2">
                      <Shield className="w-3 h-3 text-orange-500" />
                    </div>
                  )}
                  <div className={`
                    w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0
                    bg-gradient-to-br ${action.color}
                  `}>
                    <action.icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{action.label}</div>
                    <div className={`text-[10px] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{action.sublabel}</div>
                  </div>
                </div>
              </motion.button>
            ))}
          </div>
        </div>

        {/* Settings Grid with Colors */}
        <h3 className={`text-sm font-semibold mb-3 px-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>APP SETTINGS</h3>
        <div className="grid grid-cols-2 gap-3">
          {settingsSections.map((section, index) => (
            <motion.button
              key={section.label}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.05, type: "spring", stiffness: 400, damping: 25 }}
              whileHover={{ scale: 1.05, y: -4 }}
              whileTap={{ scale: 0.98 }}
              onClick={section.action}
              className="relative overflow-hidden rounded-2xl p-1 bg-gradient-to-br"
              style={{
                backgroundImage: `linear-gradient(135deg, ${section.color === 'from-blue-500 to-cyan-500' ? '#3b82f6, #06b6d4' :
                  section.color === 'from-orange-500 to-red-500' ? '#f97316, #ef4444' :
                    section.color === 'from-purple-500 to-pink-500' ? '#a855f7, #ec4899' :
                      section.color === 'from-indigo-500 to-violet-500' ? '#6366f1, #8b5cf6' :
                        section.color === 'from-emerald-500 to-green-500' ? '#10b981, #22c55e' :
                          section.color === 'from-sky-500 to-blue-500' ? '#0ea5e9, #3b82f6' :
                            section.color === 'from-amber-500 to-yellow-500' ? '#f59e0b, #eab308' :
                              '#ef4444, #f43f5e'
                  })`
              }}
            >
              <div className={`
                ${isDarkMode ? 'bg-slate-900/90' : 'bg-white/90'}
                backdrop-blur rounded-[0.9rem] p-5 text-left h-full relative
              `}>
                {/* Restricted Icon */}
                {section.restricted && (
                  <div className="absolute top-2 right-2">
                    <Shield className="w-3 h-3 text-orange-500" />
                  </div>
                )}
                <div className={`
                  w-10 h-10 rounded-xl mb-3 flex items-center justify-center
                  bg-gradient-to-br ${section.color}
                `}>
                  <section.icon className="w-5 h-5 text-white" />
                </div>
                <div className={`text-sm mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{section.label}</div>
                <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{section.sublabel}</div>
              </div>

              {/* Hover Shimmer */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/20 to-transparent"
                animate={{ x: ['-100%', '100%'] }}
                transition={{ duration: 3, repeat: Infinity, repeatDelay: 2 }}
              />
            </motion.button>
          ))}
        </div>

        {/* App Info */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className={`
            mt-6 text-center text-sm
            ${isDarkMode ? 'text-gray-500' : 'text-gray-600'}
          `}
        >
          <p>OilWellX v2.0.1</p>
          <p className="mt-1">© 2026 TGE Technology</p>
        </motion.div>
      </div>
    );
  }

  // Wells Management Section
  if (activeSection === 'wells') {
    return (
      <div className="h-full">
        <div className={`border-b p-4 flex items-center space-x-3 ${isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'}`}>
          <Button
            onClick={() => setActiveSection('main')}
            variant="outline"
            size="sm"
          >
            ← Back
          </Button>
          <div>
            <h2 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Well Management</h2>
            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Admin Only</p>
          </div>
        </div>

        {showWellForm ? (
          <ComprehensiveWellForm
            well={selectedWell}
            onSave={handleSaveWell}
            onClose={() => {
              setShowWellForm(false);
              setSelectedWell(null);
            }}
            isDarkMode={isDarkMode}
          />
        ) : (
          <AdminWellsManager
            wells={wells}
            onEditWell={(well) => {
              setSelectedWell(well);
              setShowWellForm(true);
            }}
            onDeleteWell={handleDeleteWell}
            onCreateWell={() => {
              setSelectedWell(null);
              setShowWellForm(true);
            }}
            onViewWell={handleViewWell}
          />
        )}
      </div>
    );
  }

  // User Management Section
  if (activeSection === 'users') {
    return (
      <div>
        <div className={`border-b p-4 flex items-center space-x-3 mb-4 ${isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'}`}>
          <Button
            onClick={() => setActiveSection('main')}
            variant="outline"
            size="sm"
          >
            ← Back
          </Button>
          <div>
            <h2 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>User Management</h2>
            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Admin Only</p>
          </div>
        </div>
        <UserManagement />
      </div>
    );
  }

  // Security Section
  if (activeSection === 'security') {
    return (
      <div>
        <div className={`border-b p-4 flex items-center space-x-3 mb-4 ${isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'}`}>
          <Button
            onClick={() => setActiveSection('main')}
            variant="outline"
            size="sm"
          >
            ← Back
          </Button>
          <div>
            <h2 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Security & Privacy</h2>
          </div>
        </div>
        <SecuritySetup
          userId={user.id}
          userName={user.name}
          onClose={() => setActiveSection('main')}
        />
      </div>
    );
  }

  // Data Storage & Backup Section
  if (activeSection === 'datastorage') {
    return (
      <div className={`fixed inset-0 z-50 overflow-y-auto ${isDarkMode ? 'bg-slate-950' : 'bg-white'}`}>
        <div className={`border-b p-4 flex items-center space-x-3 sticky top-0 z-10 ${isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'}`}>
          <Button
            onClick={() => setActiveSection('main')}
            variant="outline"
            size="sm"
          >
            ← Back
          </Button>
          <div>
            <h2 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Data Storage & Backup</h2>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Automated backups and disaster recovery</p>
          </div>
        </div>
        <DataStorageBackup
          user={user}
          onClose={() => setActiveSection('main')}
        />
      </div>
    );
  }

  // Vendor Management Section
  if (activeSection === 'vendors') {
    return (
      <VendorManagement
        user={{
          id: user.id,
          name: user.name,
          email: user.email,
          role: user.role
        }}
        company={{
          id: resolvedVendorCompanyId || user.companyId || user.companyId || user.companyId || 'default',
          name: resolvedVendorCompanyName || user.companyName || companyName || user.company?.name || 'Your Company'
        }}
        isDarkMode={isDarkMode}
        onClose={() => setActiveSection('main')}
      />
    );
  }

  // Escalation Matrix Section
  if (activeSection === 'escalationMatrix') {
    return (
      <div className={`fixed inset-0 z-50 overflow-y-auto ${isDarkMode ? 'bg-slate-950' : 'bg-white'}`}>
        <div className={`border-b p-4 flex items-center space-x-3 sticky top-0 z-10 ${isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'}`}>
          <Button
            onClick={() => setActiveSection('main')}
            variant="outline"
            size="sm"
          >
            ← Back
          </Button>
          <div>
            <h2 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Escalation Matrix</h2>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Configure automated alert escalation chains</p>
          </div>
        </div>
        <EscalationMatrix isDarkMode={isDarkMode} />
      </div>
    );
  }

  // Notification Policy Section
  if (activeSection === 'notificationPolicies') {
    return (
      <div className={`fixed inset-0 z-50 overflow-y-auto ${isDarkMode ? 'bg-slate-950' : 'bg-white'}`}>
        <div className={`border-b p-4 flex items-center space-x-3 sticky top-0 z-10 ${isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'}`}>
          <Button
            onClick={() => setActiveSection('main')}
            variant="outline"
            size="sm"
          >
            ← Back
          </Button>
          <div>
            <h2 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Notification Policy</h2>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Configure tenant-wide notification rules</p>
          </div>
        </div>
        <NotificationPolicyEditor isDarkMode={isDarkMode} />
      </div>
    );
  }

  // Notification Templates Section
  if (activeSection === 'notificationTemplates') {
    return (
      <div className={`fixed inset-0 z-50 overflow-y-auto ${isDarkMode ? 'bg-slate-950' : 'bg-white'}`}>
        <div className={`border-b p-4 flex items-center space-x-3 sticky top-0 z-10 ${isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'}`}>
          <Button
            onClick={() => setActiveSection('main')}
            variant="outline"
            size="sm"
          >
            ← Back
          </Button>
          <div>
            <h2 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Notification Templates</h2>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Customize alert messages</p>
          </div>
        </div>
        <NotificationTemplateEditor />
      </div>
    );
  }

  // Inventory Management Section
  if (activeSection === 'inventory') {
    return (
      <InventoryManagement
        onClose={() => setActiveSection('main')}
        currentUser={{ id: user.id, name: user.name || user.email }}
      />
    );
  }


  // Delivery Tracking Section
  if (activeSection === 'deliveryTracking') {
    return (
      <div className={`fixed inset-0 z-50 overflow-y-auto ${isDarkMode ? 'bg-slate-950' : 'bg-white'}`}>
        <div className={`border-b p-4 flex items-center space-x-3 sticky top-0 z-10 ${isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'}`}>
          <Button
            onClick={() => setActiveSection('main')}
            variant="outline"
            size="sm"
          >
            ← Back
          </Button>
          <div>
            <h2 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Delivery Tracking</h2>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Audit notification delivery status</p>
          </div>
        </div>
        <DeliveryTrackingDashboard isDarkMode={isDarkMode} />
      </div>
    );
  }

  // Notification Settings Section (User preferences)
  if (activeSection === 'notifications') {
    return (
      <NotificationSettings
        onClose={() => setActiveSection('main')}
        onSave={(_settings) => {
          toast.success('Notification settings saved');
          setActiveSection('main');
        }}
        userId={user?.id || user?.uid || 'default'}
        isDarkMode={isDarkMode}
      />
    );
  }

  // Navigation Section
  if (activeSection === 'navigation') {
    return (
      <div className={`max-w-4xl mx-auto space-y-6 p-4 ${isDarkMode ? 'bg-slate-950' : 'bg-white'}`}>
        <div className="flex items-center space-x-3 mb-4">
          <Button
            onClick={() => setActiveSection('main')}
            variant="outline"
            size="sm"
            className={isDarkMode ? 'border-slate-700 text-white hover:bg-slate-800' : ''}
          >
            ← Back
          </Button>
          <div>
            <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Navigation & Maps</h2>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Configure how you navigate to well locations</p>
          </div>
        </div>

        {/* Navigation Mode Selection */}
        <Card className={isDarkMode ? 'bg-slate-900 border-slate-700' : ''}>
          <CardHeader>
            <CardTitle className={`flex items-center gap-2 ${isDarkMode ? 'text-white' : ''}`}>
              <Navigation className="h-5 w-5" />
              Navigation Mode
            </CardTitle>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Choose how you want to navigate to well locations and routes
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Internal Navigation Option */}
            <div className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${getCurrentNavigationMode() === 'internal'
              ? isDarkMode ? 'border-blue-500 bg-blue-900/30' : 'border-blue-500 bg-blue-50'
              : isDarkMode ? 'border-slate-600 hover:border-slate-500' : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => handleNavigationModeChange('internal')}
            >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-1">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-blue-900/50' : 'bg-blue-100'}`}>
                    <Navigation className="w-5 h-5 text-blue-600" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>In-App Navigation</h3>
                  <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Use OilWellX's built-in navigation with Google Maps integration.
                    Turn-by-turn directions stay within the app.
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                      Recommended
                    </span>
                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                      Offline Capable
                    </span>
                  </div>
                </div>
                {getCurrentNavigationMode() === 'internal' && (
                  <div className="flex-shrink-0">
                    <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-4 h-4 text-white" />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* External Navigation Option */}
            <div className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${getCurrentNavigationMode() === 'external'
              ? isDarkMode ? 'border-green-500 bg-green-900/30' : 'border-green-500 bg-green-50'
              : isDarkMode ? 'border-slate-600 hover:border-slate-500' : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => handleNavigationModeChange('external')}
            >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-1">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-green-900/50' : 'bg-green-100'}`}>
                    <Smartphone className="w-5 h-5 text-green-600" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>External Maps App</h3>
                  <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Open navigation in your phone's default maps app (Google Maps, Apple Maps, Waze, etc.).
                    Get native app features and voice guidance.
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded-full">
                      Native Experience
                    </span>
                    <span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded-full">
                      Voice Guidance
                    </span>
                  </div>
                </div>
                {getCurrentNavigationMode() === 'external' && (
                  <div className="flex-shrink-0">
                    <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-4 h-4 text-white" />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Default Maps App Selection (only show when external is selected) */}
        {getCurrentNavigationMode() === 'external' && (
          <Card className={isDarkMode ? 'bg-slate-900 border-slate-700' : ''}>
            <CardHeader>
              <CardTitle className={`flex items-center gap-2 ${isDarkMode ? 'text-white' : ''}`}>
                <MapIcon className="h-5 w-5" />
                Default Maps App
              </CardTitle>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Choose which maps app to use for navigation
              </p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  onClick={() => handleMapsAppChange('google')}
                  className={`p-4 border-2 rounded-lg text-left transition-all ${user?.preferences?.defaultMapsApp === 'google'
                    ? isDarkMode ? 'border-blue-500 bg-blue-900/30' : 'border-blue-500 bg-blue-50'
                    : isDarkMode ? 'border-slate-600 hover:border-slate-500' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  aria-label="Select Google Maps"
                >
                  <div className="flex items-center gap-3">
                    <MapIcon className="w-8 h-8 text-blue-600" />
                    <div>
                      <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Google Maps</p>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Most popular choice</p>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => handleMapsAppChange('apple')}
                  className={`p-4 border-2 rounded-lg text-left transition-all ${user?.preferences?.defaultMapsApp === 'apple'
                    ? isDarkMode ? 'border-gray-500 bg-gray-700/50' : 'border-gray-500 bg-gray-50'
                    : isDarkMode ? 'border-slate-600 hover:border-slate-500' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  disabled={getPlatform() !== 'ios'}
                  aria-label="Select Apple Maps"
                >
                  <div className="flex items-center gap-3">
                    <Navigation className={`w-8 h-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} />
                    <div>
                      <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Apple Maps</p>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>iOS default</p>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => handleMapsAppChange('waze')}
                  className={`p-4 border-2 rounded-lg text-left transition-all ${user?.preferences?.defaultMapsApp === 'waze'
                    ? isDarkMode ? 'border-orange-500 bg-orange-900/30' : 'border-orange-500 bg-orange-50'
                    : isDarkMode ? 'border-slate-600 hover:border-slate-500' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  aria-label="Select Waze"
                >
                  <div className="flex items-center gap-3">
                    <Smartphone className="w-8 h-8 text-orange-500" />
                    <div>
                      <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Waze</p>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Crowd-sourced navigation</p>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => handleMapsAppChange('system')}
                  className={`p-4 border-2 rounded-lg text-left transition-all ${user?.preferences?.defaultMapsApp === 'system'
                    ? isDarkMode ? 'border-green-500 bg-green-900/30' : 'border-green-500 bg-green-50'
                    : isDarkMode ? 'border-slate-600 hover:border-slate-500' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  aria-label="Select System Default"
                >
                  <div className="flex items-center gap-3">
                    <Smartphone className="w-8 h-8 text-green-600" />
                    <div>
                      <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>System Default</p>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Use device default</p>
                    </div>
                  </div>
                </button>
              </div>

              <div className={`mt-4 p-3 border rounded-lg ${isDarkMode ? 'bg-blue-900/30 border-blue-700' : 'bg-blue-50 border-blue-200'}`}>
                <p className={`text-sm ${isDarkMode ? 'text-blue-300' : 'text-blue-800'}`}>
                  <strong>Tip:</strong> You can change your default maps app anytime by returning to this settings page.
                  Your preference is saved and remembered across all your devices.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Navigation Tips */}
        <Card className={isDarkMode ? 'bg-slate-900 border-slate-700' : ''}>
          <CardHeader>
            <CardTitle className={isDarkMode ? 'text-white' : ''}>Navigation Tips</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-start gap-3">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${isDarkMode ? 'bg-blue-900/50' : 'bg-blue-100'}`}>
                <span className="text-xs font-bold text-blue-600">i</span>
              </div>
              <div>
                <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>In-App Navigation</p>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Best for offline use and staying within OilWellX. Requires internet for initial map loading.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${isDarkMode ? 'bg-green-900/50' : 'bg-green-100'}`}>
                <span className="text-xs font-bold text-green-600">i</span>
              </div>
              <div>
                <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>External Navigation</p>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Best for voice guidance, traffic updates, and native maps features. Requires the selected app to be installed.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Appearance Section
  if (activeSection === 'appearance') {
    return (
      <div className="max-w-4xl mx-auto space-y-6 p-4">
        <div className="flex items-center space-x-3 mb-4">
          <Button
            onClick={() => setActiveSection('main')}
            variant="outline"
            size="sm"
            className={isDarkMode ? 'border-slate-700 text-white hover:bg-slate-800' : ''}
          >
            ← Back
          </Button>
          <div>
            <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Appearance & Display</h2>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Customize your viewing experience</p>
          </div>
        </div>

        {/* Theme Selection */}
        <GradientCard
          isDarkMode={isDarkMode}
          title="Theme Mode"
          className="border-0"
        >
          <div className="space-y-4">
            {/* Dark Mode Toggle */}
            <div className="flex items-center justify-between">
              <div>
                <p className={`font-medium flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  <Moon className={`h-4 w-4 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                  Dark Mode
                </p>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Reduce eye strain in low light</p>
              </div>
              <button
                onClick={() => {
                  onToggleTheme();
                  toast.success(`${!isDarkMode ? 'Dark' : 'Light'} mode enabled`);
                }}
                className={`relative w-14 h-8 rounded-full transition-colors ${isDarkMode ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                aria-label="Toggle dark mode"
                type="button"
              >
                <div
                  className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full transition-transform ${isDarkMode ? 'translate-x-6' : 'translate-x-0'
                    }`}
                />
              </button>
            </div>
          </div>
        </GradientCard>

        {/* Font Size */}
        <GradientCard
          isDarkMode={isDarkMode}
          title="Font Size"
          className="border-0"
        >
          <div className="space-y-3">
            <div>
              <label htmlFor="font-scale-slider" className={`text-sm font-medium mb-2 block ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Text Size: {Math.round(fontScale * 100)}%
              </label>
              <input
                id="font-scale-slider"
                type="range"
                min="80"
                max="200"
                step="10"
                value={fontScale * 100}
                onChange={(e) => {
                  const scale = parseInt(e.target.value) / 100;
                  setFontScale(scale);
                }}
                className={`w-full h-2 rounded-lg appearance-none cursor-pointer ${isDarkMode ? 'bg-slate-700' : 'bg-gray-200'}`}
                aria-label="Font size slider"
              />
              <div className={`flex justify-between text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                <span>Small (80%)</span>
                <span>Default (100%)</span>
                <span>Extra Large (200%)</span>
              </div>
            </div>
          </div>
        </GradientCard>

        {/* High Contrast */}
        <GradientCard
          isDarkMode={isDarkMode}
          title="Accessibility"
          className="border-0"
        >
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>High Contrast</p>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Increase contrast for better visibility</p>
              </div>
              <button
                onClick={() => {
                  setHighContrast(!highContrast);
                  toast.success(`High contrast ${!highContrast ? 'enabled' : 'disabled'}`);
                }}
                className={`relative w-14 h-8 rounded-full transition-colors ${highContrast ? 'bg-purple-600' : 'bg-gray-300'
                  }`}
                aria-label="Toggle high contrast"
                type="button"
              >
                <div
                  className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full transition-transform ${highContrast ? 'translate-x-6' : 'translate-x-0'
                    }`}
                />
              </button>
            </div>
          </div>
        </GradientCard>

        {/* Preview */}
        <Card className={`border-2 ${isDarkMode ? 'border-blue-700 bg-blue-950/30' : 'border-blue-200 bg-blue-50'}`}>
          <CardHeader>
            <CardTitle className={isDarkMode ? 'text-blue-300' : 'text-blue-900'}>Theme Preview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <p className={isDarkMode ? 'text-white' : 'text-gray-900'}>Current Mode: <strong>{isDarkMode ? 'DARK' : 'LIGHT'}</strong></p>
              <div className="flex gap-2">
                <Button size="sm">Primary Button</Button>
                <Button size="sm" variant="outline" className={isDarkMode ? 'text-white hover:bg-slate-800' : ''}>Outline Button</Button>
                <Button size="sm" variant="secondary">Secondary Button</Button>
              </div>
              <p className={`text-sm ${isDarkMode ? 'text-blue-200' : 'text-gray-600'}`}>
                Buttons are optimized for field use with 44px minimum touch targets
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Voice & AI Settings Section
  if (activeSection === 'voice') {
    return (
      <VoiceAISettings
        onClose={() => setActiveSection('main')}
        isDarkMode={isDarkMode}
      />
    );
  }

  // Help Section
  if (activeSection === 'help') {
    return (
      <div className="max-w-4xl mx-auto space-y-6 p-4">
        <div className="flex items-center space-x-3 mb-4">
          <Button
            onClick={() => setActiveSection('main')}
            variant="outline"
            size="sm"
            className={isDarkMode ? 'border-slate-700 text-white hover:bg-slate-800' : ''}
          >
            ← Back
          </Button>
          <div>
            <h2 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Help Center</h2>
          </div>
        </div>

        <GradientCard
          isDarkMode={isDarkMode}
          title="Getting Started"
          className="border-0"
        >
          <div className="space-y-2">
            <Button variant="outline" className={`w-full justify-start ${isDarkMode ? 'border-slate-700 text-white hover:bg-slate-800' : ''}`}>
              📖 Quick Start Guide
            </Button>
            <Button variant="outline" className={`w-full justify-start ${isDarkMode ? 'border-slate-700 text-white hover:bg-slate-800' : ''}`}>
              🎥 Video Tutorials
            </Button>
            <Button variant="outline" className={`w-full justify-start ${isDarkMode ? 'border-slate-700 text-white hover:bg-slate-800' : ''}`}>
              ❓ Frequently Asked Questions
            </Button>
          </div>
        </GradientCard>

        <GradientCard
          isDarkMode={isDarkMode}
          title="Contact Support"
          className="border-0"
        >
          <div className="space-y-2">
            <Button variant="outline" className={`w-full justify-start ${isDarkMode ? 'border-slate-700 text-white hover:bg-slate-800' : ''}`}>
              📧 Email Support
            </Button>
            <Button variant="outline" className={`w-full justify-start ${isDarkMode ? 'border-slate-700 text-white hover:bg-slate-800' : ''}`}>
              💬 Live Chat
            </Button>
            <Button variant="outline" className={`w-full justify-start ${isDarkMode ? 'border-slate-700 text-white hover:bg-slate-800' : ''}`}>
              📞 Call Support
            </Button>
          </div>
        </GradientCard>
      </div>
    );
  }

  // Well Check Tracking Section (Manager/Admin)
  if (activeSection === 'wellchecks') {
    return (
      <div className="max-w-6xl mx-auto space-y-6 p-4">
        <div className="flex items-center space-x-3 mb-4">
          <Button
            onClick={() => setActiveSection('main')}
            variant="outline"
            size="sm"
          >
            ← Back
          </Button>
          <div>
            <h2 className="font-bold text-gray-900">Well Check Tracking</h2>
            <p className="text-sm text-gray-600">Monitor pumper accountability and well visit history</p>
          </div>
        </div>

        <WellCheckDashboard wells={wells} pumpers={pumpers} />
      </div>
    );
  }

  return (
    <>
      {showAboutModal && <AboutModal onClose={() => setShowAboutModal(false)} />}
      {showMapsAppDialog && (
        <MapsAppSelectionDialog
          isOpen={showMapsAppDialog}
          onClose={() => setShowMapsAppDialog(false)}
          onSelectApp={handleMapsAppChange}
          currentSelection={user?.preferences?.defaultMapsApp}
          isFirstTime={true}
        />
      )}
    </>
  );
}
